import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useRef, useState } from 'react';
import { QrCode, Printer, Download } from 'lucide-react';
import CodigoQR from '../ui/CodigoQR';
import Motivo from '../experiencia/Motivo';
import { api } from '../api';
import { guardarArchivo } from '../ui/descarga';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Buscador, SelectFiltro } from '../ui/Filtros';
import CintaImpresion from './CintaImpresion';
import { contenidoDelQR } from '../accesos/enlaceDeEtiqueta';
import Modal from '../ui/Modal';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import ElegirHoja from '../accesos/ElegirHoja';
import BajarZpl from '../accesos/BajarZpl';
import { hojasOfrecidas, hojaInicial } from '../accesos/hojasDeEtiquetas';

const TIPOS = ['PRODUCTO', 'PIEZA'];

// Visor de etiquetas QR: qué códigos andan pegados a la mercancía, a qué
// están vinculados y cómo volver a imprimirlos cuando se despegan.
// `contexto` llega desde la búsqueda global: {ubicacion} entra con las
// etiquetas de ese pasillo ya filtradas.
//
// P16 · {sku, imprimir: true} llega desde «Imprimir etiqueta» (resultado del
// escaneo y ficha del producto): entra con ESE producto filtrado, marcado y
// la hoja abierta, así que solo queda elegir la hoja y «Bajar el PDF».
export default function Etiquetas({ contexto }) {
  const imprimirSku = contexto?.imprimir && contexto?.sku ? String(contexto.sku) : '';
  const [filtros, setFiltros] = useState({
    sku: imprimirSku, serie: '', ubicacion: contexto?.ubicacion || '',
    tipo: imprimirSku ? 'PRODUCTO' : '', solo_activos: true,
  });
  // Se abre la hoja UNA vez, con la primera carga: si la persona la cierra
  // y sigue buscando, no se le vuelve a abrir sola.
  const porImprimir = useRef(imprimirSku);
  const [sinEtiqueta, setSinEtiqueta] = useState(null);
  const [etiquetas, setEtiquetas] = useState([]);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [cinta, setCinta] = useState(null);
  useSubpantalla('cinta', !!cinta, () => setCinta(null));
  const [zip, setZip] = useState(null); // etiquetas cuyo .zip se está armando
  const [hoja, setHoja] = useState(null); // las que van a la hoja en PDF

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filtros)) if (v !== '' && v !== false) q.set(k, v);
    try {
      const datos = await api.get(`/qr/etiquetas?${q}`);
      setEtiquetas(datos);
      if (porImprimir.current) {
        const sku = porImprimir.current.toUpperCase();
        porImprimir.current = '';
        const suya = datos.find((e) => e.tipo === 'PRODUCTO' && String(e.sku).toUpperCase() === sku);
        if (suya) {
          setSeleccion(new Set([suya.codigo]));
          setHoja([suya]);
          return;
        }
        setSinEtiqueta(sku);
      }
      // Se descarta lo seleccionado que ya no está en pantalla: imprimir a
      // ciegas algo que el filtro dejó fuera es justo lo que confunde.
      setSeleccion((s) => new Set(datos.filter((e) => s.has(e.codigo)).map((e) => e.codigo)));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
  }, [cargar]);

  const alternar = (codigo) =>
    setSeleccion((s) => {
      const n = new Set(s);
      n.has(codigo) ? n.delete(codigo) : n.add(codigo);
      return n;
    });

  const todas = etiquetas.length > 0 && seleccion.size === etiquetas.length;
  const elegidas = etiquetas.filter((e) => seleccion.has(e.codigo));

  if (cinta) return <CintaImpresion etiquetas={cinta} onVolver={() => setCinta(null)} />;

  return (
    <div className="space-y-4">
      {hoja && <ModalHojaEtiquetas etiquetas={hoja} onCerrar={() => setHoja(null)} />}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Etiquetas QR</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Todos los códigos que andan pegados a tu mercancía. Búscalos, mira a qué están vinculados y reimprímelos.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setZip(elegidas)}
            disabled={elegidas.length === 0 || zip !== null}
            aria-describedby={elegidas.length === 0 ? 'etiquetas-falta' : undefined}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> {zip ? 'Preparando…' : 'Descargar .zip'}
          </button>
          <button
            onClick={() => setCinta(elegidas)}
            disabled={elegidas.length === 0}
            aria-describedby={elegidas.length === 0 ? 'etiquetas-falta' : undefined}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" /> Cinta de impresión
          </button>
          {/* LA HOJA DE ETIQUETAS · Accesos Fase 4.
              Es lo que faltaba y lo que de verdad se usa: un .zip de
              veinticuatro PNG no se imprime en una hoja de etiquetas, y
              componerla a mano en Word es lo que hace que nadie etiquete
              nada. Solo para productos: una pieza serializada lleva su
              número de serie, no el QR del producto. */}
          <button
            onClick={() => setHoja(elegidas.filter((e) => e.tipo === 'PRODUCTO'))}
            disabled={elegidas.filter((e) => e.tipo === 'PRODUCTO').length === 0}
            aria-describedby={elegidas.length === 0 ? 'etiquetas-falta' : undefined}
            title={elegidas.length > 0 && elegidas.every((e) => e.tipo !== 'PRODUCTO')
              ? 'La hoja de etiquetas es de productos. Lo que tienes seleccionado son piezas con número de serie.'
              : undefined}
            className="inline-flex items-center gap-1.5 bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
          >
            <Printer className="h-4 w-4" /> Hoja de etiquetas
          </button>
        </div>
      </div>
      {/* Los tres botones de arriba trabajan sobre la selección; sin ella,
          se dice en vez de dejarlos grises y mudos (Doctrina 2, regla 8). */}
      <Motivo id="etiquetas-falta" cuando={elegidas.length === 0 && etiquetas.length > 0} className="-mt-2 text-right">
        Marca una o varias etiquetas en la lista de abajo para descargarlas o imprimirlas.
      </Motivo>

      <NotaExplicativa titulo="¿Por qué hay dos clases de etiqueta?">
        La etiqueta de <strong>producto</strong> codifica el SKU y sirve para el estante o la caja: al escanearla se
        abre entrada/salida. La de <strong>pieza</strong> solo existe en productos serializados: identifica ese aparato
        concreto, no “uno como ese”, y lleva su número de serie escrito debajo. La cámara del teléfono la abre directo
        en la ficha de esa pieza, y las etiquetas de pieza ya pegadas se siguen leyendo igual en «Escanear».
        Reimprimir no cambia nada en el sistema.
      </NotaExplicativa>

      <div className="flex flex-wrap gap-2 items-center">
        <Buscador valor={filtros.sku} onChange={(v) => setFiltros((f) => ({ ...f, sku: v }))} placeholder="SKU exacto…" />
        <Buscador valor={filtros.serie} onChange={(v) => setFiltros((f) => ({ ...f, serie: v }))} placeholder="Número de serie…" />
        <Buscador valor={filtros.ubicacion} onChange={(v) => setFiltros((f) => ({ ...f, ubicacion: v }))} placeholder="Ubicación…" />
        <SelectFiltro valor={filtros.tipo} onChange={(v) => setFiltros((f) => ({ ...f, tipo: v }))} vacio="Producto y pieza" opciones={TIPOS} />
        <label className="text-sm text-gray-600 flex items-center gap-1.5">
          <input type="checkbox" checked={filtros.solo_activos} onChange={(e) => setFiltros((f) => ({ ...f, solo_activos: e.target.checked }))} />
          Solo vigentes
        </label>
      </div>

      {etiquetas.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <label className="flex items-center gap-1.5 text-gray-700 font-semibold">
            <input
              type="checkbox"
              checked={todas}
              onChange={() => setSeleccion(todas ? new Set() : new Set(etiquetas.map((e) => e.codigo)))}
            />
            Seleccionar las {etiquetas.length} de esta pantalla
          </label>
          <span className="text-gray-500">{seleccion.size} seleccionada{seleccion.size === 1 ? '' : 's'}</span>
        </div>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}
      {sinEtiqueta && (
        <div role="status" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
          {sinEtiqueta} no tiene etiqueta QR de TEGO: se identifica con su propio código de barras.
          Para ponerle una, activa su QR en la ficha del producto.
        </div>
      )}
      {cargando && <Cargando filas={4} />}

      {!cargando && etiquetas.length === 0 && (
        <EstadoVacio
          icono={QrCode}
          titulo="No hay etiquetas que coincidan"
          descripcion="Los QR de pieza solo existen en productos serializados; regístralos en “Alta rápida” o en Activos y aparecerán aquí."
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {etiquetas.map((e) => (
          <Tarjeta key={e.codigo} etiqueta={e} marcada={seleccion.has(e.codigo)} onMarcar={() => alternar(e.codigo)} />
        ))}
      </div>

      {zip && <GeneradorZip etiquetas={zip} onListo={() => setZip(null)} onError={(m) => { setError(m); setZip(null); }} />}
    </div>
  );
}

function Tarjeta({ etiqueta: e, marcada, onMarcar }) {
  return (
    <label
      className={`bg-white border rounded-xl p-3 flex flex-col items-center gap-2 cursor-pointer ${
        marcada ? 'border-[var(--color-marca)] ring-1 ring-[var(--color-marca)]' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="w-full flex items-start justify-between gap-2">
        <input type="checkbox" checked={marcada} onChange={onMarcar} />
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
          e.tipo === 'PIEZA' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700'
        }`}>
          {e.tipo}
        </span>
      </div>
      {/* AC-15 · la de una pieza lleva su URL (`…/qp/…`): la cámara del
          teléfono la abre en TEGO. Debajo se sigue leyendo la serie. */}
      <CodigoQR valor={contenidoDelQR(e)} tamano={104}
                etiqueta={`Código QR de ${e.codigo}`} />
      <div className="w-full text-center">
        <p className="font-mono text-xs text-gray-900 break-all">{e.codigo}</p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{e.producto}</p>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {[e.ubicacion, e.codigo_lote && `lote ${e.codigo_lote}`, e.estado_pieza].filter(Boolean).join(' · ') || e.sku}
        </p>
        {!e.activo && <p className="text-[11px] text-red-600 font-semibold mt-0.5">Dada de baja</p>}
      </div>
    </label>
  );
}

// Arma el .zip con un PNG por etiqueta. Los QR se pintan en canvas ocultos
// (fuera de pantalla, no display:none — un canvas oculto así no se rasteriza
// en algunos navegadores y saldría en blanco) y se leen cuando ya están.
function GeneradorZip({ etiquetas, onListo, onError }) {
  const contenedor = useRef(null);

  useEffect(() => {
    let cancelado = false;

    async function armar() {
      try {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const lienzos = contenedor.current?.querySelectorAll('canvas') || [];
        if (lienzos.length !== etiquetas.length) throw new Error('No se pudieron dibujar todas las etiquetas.');

        for (let i = 0; i < etiquetas.length; i++) {
          const datos = lienzos[i].toDataURL('image/png').split(',')[1];
          zip.file(`${nombreArchivo(etiquetas[i])}.png`, datos, { base64: true });
        }
        const blob = await zip.generateAsync({ type: 'blob' });
        if (cancelado) return;

        // guardarArchivo suelta el blob DESPUÉS de que arranque la bajada (P8).
        guardarArchivo(blob, `etiquetas-qr-${etiquetas.length}.zip`);
        onListo();
      } catch (e) {
        if (!cancelado) onError(e.message || 'No se pudo generar el .zip.');
      }
    }

    // Un fotograma de margen para que los canvas terminen de pintarse.
    const t = setTimeout(armar, 60);
    return () => { cancelado = true; clearTimeout(t); };
  }, [etiquetas, onListo, onError]);

  return (
    <div ref={contenedor} aria-hidden className="fixed -left-[9999px] top-0">
      {etiquetas.map((e) => (
        <CodigoQR key={e.codigo} valor={contenidoDelQR(e)} tamano={512} lienzo />
      ))}
    </div>
  );
}

function nombreArchivo(e) {
  return `${e.tipo === 'PIEZA' ? e.sku + '-' : ''}${e.codigo}`.replace(/[^A-Za-z0-9._-]/g, '_');
}


// =====================================================================
// LA HOJA DE ETIQUETAS EN PDF · Accesos Fase 4
// =====================================================================
// SE ELIGE LA HOJA ANTES DE IMPRIMIR, y no se supone: las hojas de
// etiquetas que se venden tienen medidas distintas, y una etiqueta
// impresa en la rejilla equivocada sale a caballo entre dos pegatinas.
//
// Y SE DICE CUAL LLEVA CODIGO DE BARRAS Y CUAL NO. En una etiqueta de
// 38 mm, un EAN-13 quedaría con barras de menos de un cuarto de
// milímetro: TEGO lo omite en vez de imprimir algo que ningún lector
// puede leer, y quien elige la hoja tiene derecho a saberlo antes.
function ModalHojaEtiquetas({ etiquetas, onCerrar }) {
  // U-02 fase a: `null` mientras carga (antes, una lista vacía que no se
  // distinguía de «no hay hojas»), y la elegida la dice el servidor con
  // `predeterminado`, no la constante '24_CARTA' de antes, que era la hoja
  // que no cabía en una carta. Las retiradas no se ofrecen.
  const [formatos, setFormatos] = useState(null);
  const [elegido, setElegido] = useState(null);
  const [contornos, setContornos] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [intento, setIntento] = useState(0);
  const [error, setError] = useState(null);
  const [bajando, setBajando] = useState(false);

  useEffect(() => {
    let vigente = true;
    api.get('/qr/formatos-etiqueta')
      .then((d) => {
        if (!vigente) return;
        const ofrecidas = hojasOfrecidas(d);
        setFormatos(ofrecidas);
        setElegido((ya) => (ya && ofrecidas.some((f) => f.codigo === ya) ? ya : hojaInicial(ofrecidas)));
      })
      .catch((e) => vigente && setErrorCarga(e));
    return () => { vigente = false; };
  }, [intento]);

  const reintentar = () => { setErrorCarga(null); setFormatos(null); setIntento((n) => n + 1); };

  const formato = (formatos || []).find((f) => f.codigo === elegido);
  const hojas = formato ? Math.ceil(etiquetas.length / formato.por_hoja) : null;

  async function bajar() {
    setBajando(true);
    setError(null);
    try {
      // SE PIDE POR POST Y SE BAJA EL CUERPO: la lista de productos
      // puede ser de quinientos y no cabe en una URL.
      const r = await fetch('/api/qr/etiquetas.pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ids: etiquetas.map((e) => e.id_producto).filter(Boolean),
          formato: elegido,
          contornos,
        }),
      });
      if (!r.ok) {
        const cuerpo = await r.json().catch(() => ({}));
        throw new Error(cuerpo.detalle || 'No se pudo armar la hoja.');
      }
      const blob = await r.blob();
      // Antes se soltaba el enlace del blob en el mismo instante del clic y
      // la bajada a veces ya no lo encontraba: «net::ERR_FAILED» en la
      // consola (LISTO-PARA-PILOTO b, P8). guardarArchivo lo suelta después.
      guardarArchivo(blob, contornos ? 'etiquetas-hoja-de-prueba.pdf' : 'etiquetas.pdf');
      onCerrar();
    } catch (e) {
      setError(e.message);
    } finally {
      setBajando(false);
    }
  }

  return (
    <Modal titulo={`Hoja de ${etiquetas.length} etiqueta${etiquetas.length === 1 ? '' : 's'}`}
           onCerrar={onCerrar}>
      <div className="space-y-4">
        {errorCarga && (
          <ErrorConReintento que="No se pudieron cargar las hojas de etiquetas"
                             porque={errorCarga} onReintentar={reintentar} />
        )}
        {!errorCarga && formatos === null && <Cargando filas={3} etiqueta="Cargando las hojas de etiquetas" />}
        {formatos !== null && formatos.length === 0 && (
          <p role="status" className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            No hay hojas de etiquetas dadas de alta. Avisa a quien administra TEGO.
          </p>
        )}
        {formatos !== null && formatos.length > 0 && (
          <ElegirHoja formatos={formatos} elegido={elegido} onElegir={setElegido}
                      contornos={contornos} onContornos={setContornos} />
        )}

        {hojas !== null && (
          <p className="text-sm text-gray-600">
            Salen <strong>{hojas}</strong> hoja{hojas === 1 ? '' : 's'} de{' '}
            {formato.por_hoja} etiquetas.
          </p>
        )}

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
            {error}
          </div>
        )}

        <button
          type="button" onClick={bajar} disabled={bajando || !formato}
          className="w-full min-h-11 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm"
        >
          {bajando ? 'Armando el PDF…' : contornos ? 'Bajar la hoja de prueba' : 'Bajar el PDF'}
        </button>

        {/* U-02 fase d: solo con una hoja de rollo; con carta o A4 no pinta nada. */}
        <BajarZpl formato={formato} ids={etiquetas.map((e) => e.id_producto).filter(Boolean)}
                  onListo={onCerrar} />
      </div>
    </Modal>
  );
}
